/**
 * GameState — hela simulationens tillstånd som ren data. Positioner i fixedpoint
 * (1/1000 world unit). Muteras endast av step(); rendering läser, skriver aldrig.
 */
import type { Family, SimConfig } from './config';
import { getUnitTier } from './config';
import { fpFromWorld } from './fixed';

export type PlayerId = 'p1' | 'p2';
export type Stance = 'follow' | 'attack' | 'defend';

export interface Base {
  owner: PlayerId;
  x: number;
  y: number;
  hp: number;
  maxHp: number;
}

export interface Mothership {
  owner: PlayerId;
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  hp: number;
  maxHp: number;
  /** Ticks kvar av reträtt (0 = normal). Spawning pausad under reträtt. */
  retreatTicksLeft: number;
  /** Ticks kvar till nästa spawn. */
  spawnCooldownTicks: number;
}

export interface Unit {
  id: number;
  owner: PlayerId;
  family: Family;
  tier: number; // 1-baserad
  x: number;
  y: number;
  hp: number;
  maxHp: number;
}

export interface UpgradeLevels {
  income: number;
  damage: number;
  health: number;
  spawnRate: number;
}

export interface PlayerState {
  id: PlayerId;
  credits: number;
  totalCreditsEarned: number;
  activeFamily: Family;
  activeTier: number;
  unlockedTiers: Record<Family, number>;
  upgrades: UpgradeLevels;
  stance: Stance;
  /** Temporär rallypunkt (fixedpoint), null = följ moderskeppet. */
  rallyPoint: { x: number; y: number } | null;
}

export interface GameState {
  tick: number;
  rngState: number;
  nextUnitId: number;
  players: Record<PlayerId, PlayerState>;
  bases: Record<PlayerId, Base>;
  motherships: Record<PlayerId, Mothership>;
  units: Unit[];
  winner: PlayerId | null;
}

export const PLAYER_IDS: readonly PlayerId[] = ['p1', 'p2'] as const;

function createPlayer(id: PlayerId, config: SimConfig): PlayerState {
  const { family, tier } = config.match.startingUnit;
  return {
    id,
    credits: config.economy.startingCredits,
    totalCreditsEarned: 0,
    activeFamily: family,
    activeTier: tier,
    unlockedTiers: { swarm: 1, ranged: 1, armor: 1, antiarmor: 1, siege: 1, flyer: 1 },
    upgrades: { income: 0, damage: 0, health: 0, spawnRate: 0 },
    stance: 'follow',
    rallyPoint: null,
  };
}

export function createInitialState(seed: number, config: SimConfig): GameState {
  const bases = {} as Record<PlayerId, Base>;
  const motherships = {} as Record<PlayerId, Mothership>;
  const players = {} as Record<PlayerId, PlayerState>;

  for (const id of PLAYER_IDS) {
    const pos = config.match.basePositions[id];
    const x = fpFromWorld(pos.x);
    const y = fpFromWorld(pos.y);
    players[id] = createPlayer(id, config);
    bases[id] = { owner: id, x, y, hp: config.match.baseHealth, maxHp: config.match.baseHealth };
    motherships[id] = {
      owner: id,
      x,
      y,
      targetX: x,
      targetY: y,
      hp: config.match.mothership.health,
      maxHp: config.match.mothership.health,
      retreatTicksLeft: 0,
      spawnCooldownTicks: spawnIntervalTicks(players[id], config),
    };
  }

  return {
    tick: 0,
    rngState: seed >>> 0,
    nextUnitId: 1,
    players,
    bases,
    motherships,
    units: [],
    winner: null,
  };
}

/** Spawnintervall i ticks för en spelare, inkl. spawnRate-upgrades. */
export function spawnIntervalTicks(player: PlayerState, config: SimConfig): number {
  const delta =
    (config.economy.upgrades.spawnRate.effectPerLevel['spawnIntervalDeltaSec'] ?? 0) *
    player.upgrades.spawnRate;
  const intervalSec = Math.max(
    config.match.minSpawnIntervalSec,
    config.match.baseSpawnIntervalSec + delta,
  );
  return Math.max(1, Math.round(intervalSec * config.match.simTicksPerSecond));
}

/** Max-HP för en ny unit av spelarens aktiva typ, inkl. health-upgrades (heltal). */
export function unitMaxHp(player: PlayerState, config: SimConfig): number {
  const base = getUnitTier(config.units, player.activeFamily, player.activeTier).hp;
  const multPerLevel = config.economy.upgrades.health.effectPerLevel['healthMult'] ?? 0;
  // Heltalsmatematik: mult i tusendelar för determinism.
  const multMilli = 1000 + Math.round(multPerLevel * 1000) * player.upgrades.health;
  return Math.trunc((base * multMilli) / 1000);
}
